<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query'

  import { huggingFaceTokensQuery } from './huggingface-tokens-store'
  import Input from '@components/Input.svelte'
  import Select from '@components/Select.svelte'

  interface Props {
    token: string | null | undefined
    tokenId: number | undefined
    saveToken: boolean
    tokenName: string
    customLabel?: string
  }

  let {
    token = $bindable(),
    tokenId = $bindable(undefined),
    saveToken = $bindable(false),
    tokenName = $bindable(''),
    customLabel = 'HuggingFace token (optional)',
  }: Props = $props()
  const id = $props.id()

  type Mode = 'existing' | 'custom'

  const queryClient = useQueryClient()
  const huggingFaceTokens = createQuery(huggingFaceTokensQuery(), queryClient)

  let tokenInputMode = $state<Mode>('custom')
  let previousTokenInputMode = $state<Mode>('custom')
  let hasSavedTokens = $derived(($huggingFaceTokens.data?.length ?? 0) > 0)

  $effect(() => {
    if (tokenId) {
      tokenInputMode = 'existing'
      token = null
      return
    }
    tokenInputMode = 'custom'
  })

  $effect(() => {
    if (!hasSavedTokens && tokenInputMode === 'existing') {
      tokenInputMode = 'custom'
    }
  })

  $effect(() => {
    if (tokenInputMode === 'existing') {
      token = null
      saveToken = false
      tokenName = ''
    } else if (previousTokenInputMode === 'existing') {
      tokenId = undefined
    }
    previousTokenInputMode = tokenInputMode
  })

  $effect(() => {
    if (!saveToken) {
      tokenName = ''
    }
  })
</script>

<div class="space-y-2">
  <fieldset class="space-y-2">
    {#if hasSavedTokens}
      <legend class="label">Token source</legend>
      <div class="flex gap-6">
        <label class="label"
          ><input type="radio" bind:group={tokenInputMode} value="existing" /> Re-use saved token</label
        >
        <label class="label"
          ><input type="radio" bind:group={tokenInputMode} value="custom" /> Use custom token</label
        >
      </div>
    {/if}

    {#if tokenInputMode === 'existing'}
      <label class="label" for={`${id}-saved-token-id`}>Saved token</label>
      <Select id={`${id}-saved-token-id`} bind:value={tokenId}>
        <option value={undefined}>Select token</option>
        {#each $huggingFaceTokens.data ?? [] as savedToken (savedToken.id)}
          <option value={savedToken.id}>{savedToken.name} ({savedToken.tokenPreview})</option>
        {/each}
      </Select>
    {:else}
      <label class="label" for={`${id}-custom-token`}>{customLabel}</label>
      <Input
        id={`${id}-custom-token`}
        type="password"
        value={token ?? ''}
        oninput={(event) => {
          token = event.currentTarget.value || null
        }}
      />

      <label class="label flex items-center gap-2">
        <input type="checkbox" bind:checked={saveToken} disabled={!token || !token.trim()} />
        Save this token
      </label>

      {#if saveToken}
        <label class="label" for={`${id}-save-token-name`}>Token name</label>
        <Input id={`${id}-save-token-name`} bind:value={tokenName} required />
      {/if}
    {/if}
  </fieldset>
</div>
